import { pathToRegexp } from "path-to-regexp"; // same package react-router uses under the hood
import { match } from "react-router-dom";

// not exported from react-router-dom 🤦‍♂️
type MatchPathOptions = {
  path?: string | string[];
  exact?: boolean;
  sensitive?: boolean;
  strict?: boolean;
};
// Follows the matchPath API as far as possible (it doesn't really make sense to return things like "isExact")

export default function matchEndOfPath(
  pathname: string,
  options: MatchPathOptions | string = {}
): null | match {
  const pathToMatch = validateArgsAreSupportedAndGetPath(pathname, options);

  const [pathnameWithoutQueryParams] = pathname.split("?");

  // React router has a cache for paths its processed. We might want to add one...
  const keys: any[] = [];
  const regex = pathToRegexp(pathToMatch, keys, {
    start: false,
    end: true
  });

  const match = regex.exec(pathnameWithoutQueryParams);

  if (!match) {
    return null;
  }

  const [url, ...values] = match;

  const isExact = pathnameWithoutQueryParams === url;

  return {
    path: pathToMatch,
    // the path used to match
    url: pathToMatch === "/" && url === "" ? "/" : url,
    // the matched portion of the URL
    isExact,
    params: keys.reduce((memo, key, index) => {
      memo[key.name] = values[index];
      return memo;
    }, {})
  };
}

function validateArgsAreSupportedAndGetPath(
  pathname: string,
  options: MatchPathOptions | string | Array<string> = {}
) {
  if (!options) {
    throw new MissingPathError();
  }

  if (Array.isArray(options)) {
    throw new ArrayNotSupportedError();
  }

  let _pathToMatch: string | typeof undefined | string[] =
    typeof options === "string" ? options : options.path;

  if (!_pathToMatch) {
    throw new MissingPathError();
  }

  if (Array.isArray(_pathToMatch)) {
    throw new ArrayNotSupportedError();
  }

  if (typeof options !== "string") {
    const { path, exact, ...otherOptions } = options;

    if (Object.keys(otherOptions).length) {
      throw new Error(
        `Some options are not supported by 'matchEndOfPath' yet, received the following non-supported options: ${JSON.stringify(
          otherOptions
        )}`
      );
    }
  }

  return _pathToMatch;
}

class MissingPathError extends Error {
  constructor() {
    super(`You need to pass a path to match to 'matchEndOfPath'`);
  }
}

class ArrayNotSupportedError extends Error {
  constructor() {
    super(
      `'matchEndOfPath' received an array of paths to match, this is not supported yet.`
    );
  }
}
